import React, { useState, useEffect, useContext, useCallback } from "react";
import { UserContext } from "../../../../contexts/CurrentUserContext";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { registerLocale, setDefaultLocale } from "react-datepicker";
import ru from "date-fns/locale/ru";
import Select from "react-select";
import { getAllGroups, getUsersSummary } from "../../../../api/api";
import { getAllReports } from "../../../../api/reportsApi";
import {
  createTimesheetApproval,
  getTimesheetApprovals,
} from "../../../../api/timesheetApi";
import "./DepartmentReports.css";
import Preloader from "../../../Preloader/Preloader";
import ExcelJS from "exceljs";
import { getCalendar } from "../../../../api/api";
import { formatHoursWithRounding } from "../../../../utils/timeUtils";

const SPECIAL_JOB_TYPES = {
  1238: { name: "Отпуск без содержания", code: "БС" },
  1236: { name: "Больничный", code: "БЛ" },
  1237: { name: "Отпуск", code: "ОТ" },
};

const BUSINESS_TRIP_IDS = [
  1242, 1243, 1825, 1826, 1827, 1828, 1829, 1830, 1831,
]; // ID командировок

registerLocale("ru", ru);
setDefaultLocale("ru");

const DepartmentReports = () => {
  const { userData } = useContext(UserContext);
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  // const [departments, setDepartments] = useState([]); // Закомментировано - не используется
  const [selectedDepartment, setSelectedDepartment] = useState(null);
  const [departmentUsers, setDepartmentUsers] = useState([]);
  const [reports, setReports] = useState([]);
  // const [loading, setLoading] = useState(true); // Закомментировано - не используется
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [calendarData, setCalendarData] = useState({});
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [canSelectDepartment, setCanSelectDepartment] = useState(false);
  const [isLoadingTable, setIsLoadingTable] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [showConfirmationTooltip, setShowConfirmationTooltip] = useState(false);
  const [highlightProblems, setHighlightProblems] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalUsers, setApprovalUsers] = useState([]);
  const [currentTimesheetStatus, setCurrentTimesheetStatus] = useState(null); // Текущий статус табеля с сервера
  const [showSuccessNotification, setShowSuccessNotification] = useState(false);

  // Объединяем два useEffect в один для корректной последовательности действий
  useEffect(() => {
    if (userData) {
      // Проверяем, есть ли у пользователя уровень доступа АДМС - это самый высокий приоритет
      const hasAdmsAccess = userData.access_levels.some(
        (level) => level.name === "АДМС",
      );

      // Если у пользователя есть АДМС, он получает полный доступ ко всем отделам
      if (hasAdmsAccess) {
        setCanSelectDepartment(true);
        const loadAllDepartments = async () => {
          try {
            setIsLoadingDepartments(true);
            const allGroups = await getAllGroups();
            const departments = allGroups.filter((group) => group.is_active);
            setAvailableDepartments(departments);
            // setDepartments(departments); // Закомментировано - не используется

            // Устанавливаем первый отдел по умолчанию
            if (departments.length > 0) {
              setSelectedDepartment(departments[0]);
            }

            setIsLoadingDepartments(false);
          } catch (error) {
            console.error("Ошибка при загрузке отделов:", error);
            setIsLoadingDepartments(false);
          }
        };
        loadAllDepartments();
        return; // Выходим из useEffect, так как АДМС имеет полный доступ
      }

      // Для остальных пользователей применяем обычную логику
      const allowedAccessLevels = ["ФИН", "ИК", "АДМП", "НО"];
      const hasAccess =
        userData.access_levels.some((level) =>
          allowedAccessLevels.includes(level.name),
        ) ||
        userData.id === 167 ||
        userData.id === 229 ||
        userData.id === 63 || // HR менеджер
        userData.id === 131; // Бухгалтер

      // Временно устанавливаем базовые права доступа
      const baseCanSelect =
        hasAccess ||
        userData.id === 21 ||
        userData.id === 54 ||
        userData.id === 30 ||
        userData.id === 68;

      // Позже обновим это значение после загрузки отделов
      setCanSelectDepartment(baseCanSelect);

      const loadDepartments = async () => {
        try {
          setIsLoadingDepartments(true);
          let departments;

          if (userData.id === 167 || userData.id === 229) {
            // Массив разрешенных ID отделов для пользователей 167 и 229
            const allowedDepartmentIds = [62, 63, 64, 65, 66, 67, 68, 69];
            const allGroups = await getAllGroups();
            departments = allGroups.filter(
              (group) =>
                group.is_active && allowedDepartmentIds.includes(group.id),
            );
          } else if (userData.id === 54) {
            // Массив разрешенных ID отделов для пользователя 54
            const allowedDepartmentIds = [61, 62, 63, 64, 65, 66, 67, 68, 69];
            const allGroups = await getAllGroups();
            departments = allGroups.filter(
              (group) =>
                group.is_active && allowedDepartmentIds.includes(group.id),
            );
          } else if (userData.id === 12) {
            // Массив разрешенных ID отделов для пользователя 12
            const allowedDepartmentIds = [
              51, 52, 53, 54, 55, 56, 57, 58, 59, 60,
            ];
            const allGroups = await getAllGroups();
            departments = allGroups.filter(
              (group) =>
                group.is_active && allowedDepartmentIds.includes(group.id),
            );
          } else if (userData.id === 68) {
            // Для пользователя 68: его собственные отделы + отдел 58
            const userOwnGroups = userData.departments.flatMap(
              (dept) => dept.groups || [],
            );
            const userOwnGroupIds = userOwnGroups.map((group) => group.id);
            const allowedDepartmentIds = [...userOwnGroupIds, 58];
            const allGroups = await getAllGroups();
            departments = allGroups.filter(
              (group) =>
                group.is_active && allowedDepartmentIds.includes(group.id),
            );
          } else if (userData.id === 30) {
            // Для пользователя с ID 30 показываем отделы с ID 53 и 52
            const allGroups = await getAllGroups();
            departments = allGroups.filter(
              (group) =>
                group.is_active && (group.id === 53 || group.id === 52),
            );
          } else if (userData.id === 21) {
            const allGroups = await getAllGroups();
            departments = allGroups.filter(
              (group) => group.is_active && [60, 68, 69].includes(group.id),
            );
          } else if (hasAccess) {
            // Для пользователей с административными правами или начальников отделов
            const isHeadOfDepartment = userData.access_levels.some(
              (level) => level.name === "НО",
            );

            if (isHeadOfDepartment) {
              // Для начальников отделов показываем только их отделы
              const allGroups = userData.departments.flatMap(
                (dept) => dept.groups || [],
              );
              // Показываем все отделы, если is_active не определен или true
              departments = allGroups.filter(
                (group) => group.is_active !== false,
              );
            } else {
              // Для остальных администраторов показываем все отделы
              const allGroups = await getAllGroups();
              departments = allGroups.filter((group) => group.is_active);
            }
          } else {
            // Получаем все группы из всех департаментов
            const allGroups = userData.departments.flatMap(
              (dept) => dept.groups || [],
            );
            // Показываем все отделы, если is_active не определен или true
            departments = allGroups.filter(
              (group) => group.is_active !== false,
            );
          }

          setAvailableDepartments(departments);
          // setDepartments(departments); // Закомментировано - не используется

          // Обновляем права на выбор отдела
          // Если у начальника отдела только один отдел, отключаем селект
          const isHeadOfDepartment = userData.access_levels.some(
            (level) => level.name === "НО",
          );

          if (isHeadOfDepartment && departments.length === 1) {
            setCanSelectDepartment(false);
          } else {
            setCanSelectDepartment(baseCanSelect);
          }

          // Находим активный отдел пользователя из его собственных отделов
          const userOwnGroups = userData.departments.flatMap(
            (dept) => dept.groups || [],
          );
          const userActiveGroup = userOwnGroups.find(
            (group) => group.is_active !== false,
          );

          // Устанавливаем отдел по умолчанию:
          // - если есть активный отдел пользователя И он доступен в списке разрешенных отделов, устанавливаем его
          // - иначе ищем первый отдел пользователя в списке доступных отделов
          // - если ничего не найдено, берем первый доступный отдел
          if (
            userActiveGroup &&
            departments.some((dept) => dept.id === userActiveGroup.id)
          ) {
            console.log(
              "Выбран активный отдел пользователя:",
              userActiveGroup.name,
            );
            setSelectedDepartment(userActiveGroup);
          } else {
            // Ищем первый отдел пользователя в списке доступных отделов
            const userFirstGroup = userOwnGroups.find((userGroup) =>
              departments.some((dept) => dept.id === userGroup.id),
            );

            if (userFirstGroup) {
              console.log(
                "Выбран первый отдел пользователя:",
                userFirstGroup.name,
              );
              setSelectedDepartment(userFirstGroup);
            } else if (departments.length > 0) {
              console.log(
                "Выбран первый доступный отдел:",
                departments[0].name,
              );
              setSelectedDepartment(departments[0]);
            } else {
              console.log("Нет доступных отделов");
            }
          }

          setIsLoadingDepartments(false);
        } catch (error) {
          console.error("Ошибка при загрузке отделов:", error);
          setIsLoadingDepartments(false);
        }
      };

      loadDepartments();
    }
  }, [userData]);

  // Вспомогательная функция для проверки существования табеля
  // Согласно серверной логике, нельзя создать новое согласование,
  // если уже существует запись для этого отдела и месяца (с любым статусом)
  const hasExistingTimesheet = (timesheets) => {
    return timesheets && timesheets.length > 0;
  };

  // Функция для загрузки статуса табеля с сервера
  const loadTimesheetStatus = useCallback(async () => {
    if (!selectedDepartment || !selectedMonth) {
      console.log(
        "loadTimesheetStatus: Отдел или месяц не выбраны, сбрасываем статус",
      );
      setCurrentTimesheetStatus(null);
      return;
    }

    try {
      const params = {
        group_id: selectedDepartment.id,
        year: selectedMonth.getFullYear(),
        month: selectedMonth.getMonth() + 1,
      };

      console.log("🔍 Загрузка статуса табеля для:", {
        department: selectedDepartment.name,
        ...params,
      });

      const timesheets = await getTimesheetApprovals(params);

      console.log("📋 Получены табели с сервера:", {
        count: timesheets.length,
        data: timesheets,
      });

      // Проверяем наличие любого табеля для этого отдела и месяца
      // Серверная логика не позволяет создавать новое согласование,
      // если уже существует запись (с любым статусом)
      if (timesheets.length > 0) {
        const existingTimesheet = timesheets[0]; // Берем первый (должен быть только один)

        console.log("✅ Найден существующий табель:", {
          id: existingTimesheet.id,
          status: existingTimesheet.status,
          created_at: existingTimesheet.created_at,
          initiator: existingTimesheet.initiator,
        });

        setCurrentTimesheetStatus(existingTimesheet);
      } else {
        console.log("❌ Табелей не найдено - можно создать новый");
        setCurrentTimesheetStatus(null);
      }
    } catch (error) {
      console.error("❌ Ошибка при загрузке статуса табеля:", error);
      setCurrentTimesheetStatus(null);
    }
  }, [selectedDepartment, selectedMonth]);

  // Оставляем отдельный useEffect для загрузки пользователей и отчетов
  useEffect(() => {
    const loadDepartmentUsers = async () => {
      if (!selectedDepartment) return;

      try {
        setIsLoadingTable(true);
        setIsLoading(true);

        const response = await getUsersSummary({
          groupId: selectedDepartment.id,
          isActive: true,
          pageSize: 100,
        });

        const usersInDepartment = response.users;
        setDepartmentUsers(usersInDepartment);

        const firstDayOfMonth = new Date(
          selectedMonth.getFullYear(),
          selectedMonth.getMonth(),
          1,
        )
          .toISOString()
          .split("T")[0];

        // Добавляем логирование запросов отчетов
        console.log("Запрашиваем отчеты для месяца:", firstDayOfMonth);

        const allReports = await Promise.all(
          usersInDepartment.map(async (user) => {
            const userReports = await getAllReports(user.id, firstDayOfMonth);
            console.log(`Получены отчеты для пользователя ${user.surname}:`, {
              totalReports: userReports.length,
              dates: userReports.map((r) =>
                new Date(r.fact_date_start).toLocaleDateString(),
              ),
            });
            return userReports;
          }),
        );

        const flattenedReports = allReports.flat();
        console.log("Все отчеты после обработки:", {
          total: flattenedReports.length,
          byDate: flattenedReports.reduce((acc, report) => {
            const date = new Date(report.fact_date_start).toLocaleDateString();
            acc[date] = (acc[date] || 0) + 1;
            return acc;
          }, {}),
        });

        setReports(flattenedReports);

        // Загружаем статус табеля
        await loadTimesheetStatus();
      } catch (error) {
        console.error("Ошибка при загрузке данных:", error);
      } finally {
        setIsLoadingTable(false);
        setIsLoading(false);
      }
    };

    loadDepartmentUsers();
  }, [selectedDepartment, selectedMonth, loadTimesheetStatus]);

  // Добавьте функцию для получения первого и последнего дня месяца
  const getMonthRange = (date) => {
    const firstDay = new Date(
      Date.UTC(date.getFullYear(), date.getMonth(), 1, 12, 0, 0, 0),
    );
    const lastDay = new Date(
      Date.UTC(date.getFullYear(), date.getMonth() + 1, 0, 12, 0, 0, 0),
    );
    return {
      firstDay: firstDay.toISOString().split("T")[0],
      lastDay: lastDay.toISOString().split("T")[0],
    };
  };

  // Обновите useEffect для загрузки календаря
  useEffect(() => {
    const loadCalendarData = async () => {
      try {
        const { firstDay, lastDay } = getMonthRange(selectedMonth);
        const calendar = await getCalendar(firstDay, lastDay);
        const calendarMap = {};
        calendar.forEach((day) => {
          calendarMap[day.date] = day;
        });
        console.log("Calendar data:", calendarMap);
        setCalendarData(calendarMap);
      } catch (error) {
        console.error("Ошибка при загрузке календаря:", error);
      }
    };
    loadCalendarData();
  }, [selectedMonth]); // Добавляем selectedMonth в зависимости

  // Добавьте эффект для отслеживания позиции курсора
  useEffect(() => {
    const handleMouseMove = (e) => {
      document.documentElement.style.setProperty("--mouse-x", `${e.clientX}px`);
      document.documentElement.style.setProperty("--mouse-y", `${e.clientY}px`);
    };

    document.addEventListener("mousemove", handleMouseMove);
    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, []);

  // const handleMonthChange = (date) => {
  //   setSelectedMonth(date);
  // }; // Закомментировано - не используется

  const generateTableHeaders = () => {
    const daysInMonth = new Date(
      selectedMonth.getFullYear(),
      selectedMonth.getMonth() + 1,
      0,
    ).getDate();

    return Array.from({ length: daysInMonth }, (_, i) => {
      // Создаем дату с учетом часового пояса
      const currentDate = new Date(
        Date.UTC(
          selectedMonth.getFullYear(),
          selectedMonth.getMonth(),
          i + 1,
          12, // устанавливаем полдень по UTC
          0,
          0,
          0,
        ),
      );

      const dateString = currentDate.toISOString().split("T")[0];
      const dayData = calendarData[dateString];

      return {
        day: i + 1,
        isWeekend: dayData ? !dayData.workday_flag : false,
        eventName: dayData?.event_name,
      };
    });
  };

  // Добавим функцию форматирования времени
  const formatTime = (hours) => {
    return formatHoursWithRounding(hours);
  };

  // Функция для форматирования имени сотрудника в формат "Фамилия И."
  const formatEmployeeName = (surname, name) => {
    if (!name) return surname;
    return `${surname} ${name.charAt(0)}.`;
  };

  const formatCellValue = (reports, isWeekend) => {
    if (!reports || reports.length === 0) {
      return isWeekend ? "ВЫХ" : "";
    }

    // Проверяем наличие специальных типов работ
    const specialReport = reports.find(
      (report) => SPECIAL_JOB_TYPES[report.portal_job_type?.id],
    );
    if (specialReport) {
      return SPECIAL_JOB_TYPES[specialReport.portal_job_type.id].code;
    }

    // Проверяем наличие командировок
    const hasBusinessTrip = reports.some((report) =>
      BUSINESS_TRIP_IDS.includes(report.portal_job_type?.id),
    );
    if (hasBusinessTrip) {
      return "К";
    }

    // Считаем общее время для обычных отчетов (исключая командировки)
    const totalHours = reports.reduce((sum, report) => {
      if (
        !SPECIAL_JOB_TYPES[report.portal_job_type?.id] &&
        !BUSINESS_TRIP_IDS.includes(report.portal_job_type?.id)
      ) {
        return sum + report.time_spent / 3600;
      }
      return sum;
    }, 0);

    // Если есть отчеты, всегда показываем часы, даже в выходной день
    // "ВЫХ" показываем только если нет отчетов
    return totalHours > 0 ? formatTime(totalHours) : "";
  };

  const isOvertimeApproved = (report) => {
    if (!report.is_overtime || !report.overtime_statuses) return false;

    // Проверяем, что все статусы "Согласован"
    return Object.values(report.overtime_statuses).every(
      (status) => status === "Согласован",
    );
  };

  const renderTimesheet = () => {
    const daysInMonth = generateTableHeaders();
    const totalDailyHours = {};
    const overtimeDays = {};
    const userOvertimeTotals = {};

    daysInMonth.forEach((dayData) => {
      totalDailyHours[dayData.day] = 0;
      overtimeDays[dayData.day] = false;
    });

    // Сортируем пользователей по алфавиту (по фамилии, затем по имени)
    const sortedUsers = [...departmentUsers].sort((a, b) => {
      const surnameA = (a.surname || "").toLowerCase();
      const surnameB = (b.surname || "").toLowerCase();

      if (surnameA !== surnameB) {
        return surnameA.localeCompare(surnameB, "ru");
      }

      // Если фамилии одинаковые, сортируем по имени
      const nameA = (a.name || "").toLowerCase();
      const nameB = (b.name || "").toLowerCase();
      return nameA.localeCompare(nameB, "ru");
    });

    // Фильтруем пользователей: оставляем только тех, у кого есть отчеты за выбранный месяц
    const usersWithReports = sortedUsers.filter((user) => {
      const userReports = reports.filter((report) => {
        const reportDate = new Date(report.fact_date_start);
        return (
          report.user.id === user.id &&
          reportDate.getMonth() === selectedMonth.getMonth() &&
          reportDate.getFullYear() === selectedMonth.getFullYear()
        );
      });
      return userReports.length > 0;
    });

    return (
      <div className="timesheet-table-container">
        <table className="timesheet-table">
          <thead>
            <tr>
              <th>ФИО</th>
              {daysInMonth.map((dayData) => (
                <th
                  key={dayData.day}
                  className={dayData.isWeekend ? "weekend" : ""}
                  data-tooltip={dayData.eventName || undefined}
                >
                  {dayData.day}
                </th>
              ))}
              <th className="rotated-header" data-content="Рабочие часы"></th>
              <th className="rotated-header" data-content="Переработки"></th>
              <th className="rotated-header" data-content="Отгулы"></th>
            </tr>
          </thead>
          <tbody>
            {usersWithReports.map((user) => {
              const userReports = reports.filter((report) => {
                const reportDate = new Date(report.fact_date_start);
                return (
                  report.user.id === user.id &&
                  reportDate.getMonth() === selectedMonth.getMonth() &&
                  reportDate.getFullYear() === selectedMonth.getFullYear()
                );
              });

              const dailyHours = {};
              const dailyRemoteHours = {};
              const dailyOvertimes = {};
              const dailyDayOffs = {};
              const dailyRemote = {};
              let overtimeTotal = 0;
              let dayOffTotal = 0;

              userReports.forEach((report) => {
                const reportDate = new Date(report.fact_date_start);
                const day = reportDate.getDate();
                const hours = report.time_spent / 3600;

                if (report.is_overtime) {
                  if (isOvertimeApproved(report)) {
                    dailyOvertimes[day] = true;
                    overtimeTotal += hours;
                  }
                } else if (report.is_day_off) {
                  dailyDayOffs[day] = true;
                  dayOffTotal += hours;
                } else {
                  if (report.is_remote) {
                    dailyRemoteHours[day] =
                      (dailyRemoteHours[day] || 0) + hours;
                    dailyRemote[day] = true;
                  } else {
                    dailyHours[day] = (dailyHours[day] || 0) + hours;
                  }
                }
              });

              // const monthlyTotal = Object.entries(dailyHours).reduce(
              //   (acc, [day, hours]) => {
              //     const dayReports = userReports.filter((report) => {
              //       const reportDate = new Date(report.fact_date_start);
              //       return reportDate.getDate() === parseInt(day);
              //     });

              //     // Считаем сумму обычных часов за день (не переработки)
              //     const normalHours = dayReports.reduce((sum, report) => {
              //       if (!report.is_overtime) {
              //         return sum + (report.time_spent || 0) / 3600;
              //       }
              //       return sum;
              //     }, 0);

              //     return acc + normalHours;
              //   },
              //   0
              // ); // Закомментировано - не используется

              userOvertimeTotals[user.id] = overtimeTotal;

              return (
                <tr key={user.id}>
                  <td>
                    {formatEmployeeName(user.surname, user.name)}
                    {user.roles &&
                      user.roles.some((role) => /стаж[её]р/i.test(role)) && (
                        <>
                          <br />
                          <span className="trainee-label">(Стажер)</span>
                        </>
                      )}
                  </td>
                  {daysInMonth.map((dayData) => {
                    // Находим все отчеты пользователя за этот день
                    const dayReports = userReports.filter((report) => {
                      const reportDate = new Date(report.fact_date_start);
                      return reportDate.getDate() === dayData.day;
                    });

                    // Проверяем наличие отчета, заполненного не сотрудником, исключая спец. типы работ, отгулы и командировки
                    const hasNotSelfEntered = dayReports.some(
                      (r) =>
                        r.self_entered === false &&
                        !SPECIAL_JOB_TYPES[r.portal_job_type?.id] &&
                        !r.is_day_off &&
                        !BUSINESS_TRIP_IDS.includes(r.portal_job_type?.id), // Исключаем командировки
                    );

                    // Разделяем отчеты: обычные (без отгулов), отгулы, удалёнка, переработки
                    const dayOffReportsForCell = dayReports.filter(
                      (report) => report.is_day_off,
                    );
                    const dayOffHoursForCell = dayOffReportsForCell.reduce(
                      (sum, report) => sum + report.time_spent / 3600,
                      0,
                    );
                    const normalReports = dayReports.filter(
                      (report) =>
                        !report.is_overtime &&
                        !report.is_remote &&
                        !report.is_day_off,
                    );
                    const combinedRemoteOvertimeReports = dayReports.filter(
                      (report) =>
                        report.is_overtime &&
                        report.is_remote &&
                        isOvertimeApproved(report),
                    );
                    const remoteOnlyReports = dayReports.filter(
                      (report) => !report.is_overtime && report.is_remote,
                    );
                    const overtimeOnlyReports = dayReports.filter(
                      (report) =>
                        report.is_overtime &&
                        !report.is_remote &&
                        isOvertimeApproved(report),
                    );
                    const remoteReports = dayReports.filter(
                      (report) => !report.is_overtime && report.is_remote,
                    );
                    const overtimeReports = dayReports.filter(
                      (report) =>
                        report.is_overtime && isOvertimeApproved(report),
                    );

                    const hasCombinedRemoteOvertime =
                      combinedRemoteOvertimeReports.length > 0;
                    const hasSeparateRemoteAndOvertime =
                      remoteOnlyReports.length > 0 &&
                      overtimeOnlyReports.length > 0;
                    const combinedHours = combinedRemoteOvertimeReports.reduce(
                      (sum, report) => sum + report.time_spent / 3600,
                      0,
                    );
                    const remoteOnlyHours = remoteOnlyReports.reduce(
                      (sum, report) => sum + report.time_spent / 3600,
                      0,
                    );
                    const overtimeOnlyHours = overtimeOnlyReports.reduce(
                      (sum, report) => sum + report.time_spent / 3600,
                      0,
                    );

                    // Проверяем наличие специальных типов работ
                    const specialReport = normalReports.find(
                      (report) => SPECIAL_JOB_TYPES[report.portal_job_type?.id],
                    );
                    let specialClass = "";
                    if (specialReport) {
                      const code =
                        SPECIAL_JOB_TYPES[specialReport.portal_job_type.id]
                          .code;
                      if (code === "БЛ") specialClass = "cell-bl";
                      if (code === "ОТ") specialClass = "cell-ot";
                      if (code === "БС") specialClass = "cell-bs";
                    }

                    // const hasSpecialType = !!specialReport; // Закомментировано - не используется
                    // const normalHours = hasSpecialType
                    //   ? 0
                    //   : normalReports.reduce(
                    //       (sum, report) => sum + report.time_spent / 3600,
                    //       0
                    //     ); // Закомментировано - не используется
                    const overtimeHours = overtimeReports.reduce(
                      (sum, report) => sum + report.time_spent / 3600,
                      0,
                    );
                    const normalCellValue = formatCellValue(
                      normalReports,
                      dayData.isWeekend,
                    );
                    const remoteCellValue = formatCellValue(
                      remoteReports,
                      dayData.isWeekend,
                    );

                    // Проверяем, есть ли и обычные, и удаленные отчеты
                    const hasBothTypes =
                      normalReports.length > 0 && remoteReports.length > 0;

                    // Отгул + удалёнка в один день (без обычного и переработок) — диагональ голубой/фиолетовый
                    const hasDayOffAndRemote =
                      dayOffHoursForCell > 0 &&
                      remoteOnlyHours > 0 &&
                      normalReports.length === 0 &&
                      overtimeHours === 0;

                    // Отгул + обычный отчёт в один день — диагональ зелёный/голубой
                    const hasDayOffAndNormal =
                      dayOffHoursForCell > 0 &&
                      normalReports.length > 0 &&
                      remoteReports.length === 0 &&
                      overtimeHours === 0;

                    // Отгул + переработка в один день — диагональ голубой (отгул) / жёлтый (переработка)
                    const hasDayOffAndOvertime =
                      dayOffHoursForCell > 0 &&
                      overtimeHours > 0 &&
                      normalReports.length === 0 &&
                      remoteReports.length === 0;

                    // Три вида в один день: обычный + удалёнка + переработка → три горизонтальные полосы
                    const hasThreeTypes =
                      normalReports.length > 0 &&
                      (remoteOnlyReports.length > 0 ||
                        combinedRemoteOvertimeReports.length > 0) &&
                      (overtimeOnlyReports.length > 0 ||
                        combinedRemoteOvertimeReports.length > 0);

                    // Обычный отчёт + только комбинированный (удалёнка 0 + переработка N) — показываем норма + диагональ 0/5
                    const hasThreeTypesButOnlyCombined =
                      hasThreeTypes &&
                      remoteOnlyReports.length === 0 &&
                      overtimeOnlyReports.length === 0 &&
                      combinedRemoteOvertimeReports.length > 0;

                    // Суммы для отображения: комбинированные отчёты (удалёнка+переработка) считаем только как переработка
                    const totalRemoteHoursForCell = remoteOnlyHours;
                    const totalOvertimeHoursForCell =
                      overtimeOnlyHours + combinedHours;
                    // Диагональ с двумя значениями: когда есть и удалёнка, и переработка, и при этом
                    // либо два отдельных отчёта, либо комбинированный + отдельная удалёнка/переработка,
                    // либо только комбинированный отчёт (показываем 0 и N по диагонали)
                    const hasRemoteAndOvertimeDiagonal =
                      totalOvertimeHoursForCell > 0 &&
                      (totalRemoteHoursForCell > 0
                        ? hasSeparateRemoteAndOvertime ||
                          remoteOnlyReports.length > 0 ||
                          overtimeOnlyReports.length > 0
                        : hasCombinedRemoteOvertime);

                    // В диагональной ячейке одно значение — центрируем (0:00 не показываем, поэтому только переработка)
                    const hasSingleValueInDiagonal =
                      totalRemoteHoursForCell === 0 &&
                      totalOvertimeHoursForCell > 0;

                    // Формируем текст для всплывающей подсказки
                    let tooltipText = dayData.eventName || undefined;
                    if (overtimeHours > 0) {
                      tooltipText = tooltipText
                        ? `${tooltipText}\nПереработка: ${formatTime(
                            overtimeHours,
                          )}`
                        : `Переработка: ${formatTime(overtimeHours)}`;
                    }

                    // Добавляем информацию об удаленной работе в тултип (только отчёты без переработки; комбинированные считаем переработкой)
                    const totalRemoteHours = remoteOnlyHours;
                    if (totalRemoteHours > 0) {
                      tooltipText = tooltipText
                        ? `${tooltipText}\nУдаленно: ${formatTime(totalRemoteHours)}`
                        : `Удаленно: ${formatTime(totalRemoteHours)}`;
                    }

                    // Добавляем информацию о командировке в тултип
                    const hasBusinessTrip = normalReports.some((report) =>
                      BUSINESS_TRIP_IDS.includes(report.portal_job_type?.id),
                    );
                    if (hasBusinessTrip) {
                      tooltipText = tooltipText
                        ? `${tooltipText}\nКомандировка`
                        : "Командировка";
                    }

                    const isEmptyCell =
                      !normalCellValue &&
                      !remoteCellValue &&
                      !specialReport &&
                      overtimeHours === 0 &&
                      dayOffHoursForCell === 0;

                    const isProblematic = isProblematicCell(
                      dayReports,
                      dayData,
                    );

                    return (
                      <td
                        key={dayData.day}
                        className={`
                          ${dayData.isWeekend ? "weekend" : ""}
                          ${hasThreeTypes && !hasThreeTypesButOnlyCombined ? "cell-three-stripes" : ""}
                          ${hasThreeTypesButOnlyCombined ? "cell-normal-plus-diagonal" : ""}
                          ${hasRemoteAndOvertimeDiagonal && !hasThreeTypes ? "cell-split-remote-overtime" : ""}
                          ${hasRemoteAndOvertimeDiagonal && !hasThreeTypes && hasSingleValueInDiagonal ? "cell-split-remote-overtime--single" : ""}
                          ${hasCombinedRemoteOvertime && !hasRemoteAndOvertimeDiagonal && !hasThreeTypes ? "cell-combined-remote-overtime" : ""}
                          ${overtimeHours > 0 && !hasCombinedRemoteOvertime && !hasRemoteAndOvertimeDiagonal && !hasThreeTypes ? "split-cell" : ""}
                          ${(hasBothTypes || (overtimeHours > 0 && remoteReports.length > 0)) && !hasThreeTypes && !hasCombinedRemoteOvertime && !hasRemoteAndOvertimeDiagonal ? "split-cell-remote" : ""}
                          ${hasDayOffAndRemote ? "cell-split-dayoff-remote" : ""}
                          ${hasDayOffAndNormal ? "cell-split-normal-dayoff" : ""}
                          ${hasDayOffAndOvertime ? "cell-split-dayoff-overtime" : ""}
                          ${dailyDayOffs[dayData.day] && !hasDayOffAndRemote && !hasDayOffAndNormal && !hasDayOffAndOvertime ? "dayoff-cell_true" : ""}
                          ${
                            dailyRemote[dayData.day] && !hasBothTypes && !hasDayOffAndRemote
                              ? "remote-cell_true"
                              : ""
                          }
                          ${specialClass}
                          ${isEmptyCell ? "empty-cell" : ""}
                          ${hasBusinessTrip ? "business-trip" : ""}
                          ${isProblematic ? "problematic-cell" : ""}
                        `}
                        data-tooltip={tooltipText}
                      >
                        <span>
                          {specialReport ? (
                            <div className={specialClass}>
                              <span
                                className={
                                  hasNotSelfEntered ? "not-self-entered" : ""
                                }
                              >
                                {
                                  SPECIAL_JOB_TYPES[
                                    specialReport.portal_job_type.id
                                  ].code
                                }
                              </span>
                            </div>
                          ) : hasThreeTypesButOnlyCombined ? (
                            <div className="cell-normal-plus-diagonal-inner">
                              <div className="cell-normal-plus-diagonal-top">
                                <span
                                  className={
                                    hasNotSelfEntered ? "not-self-entered" : ""
                                  }
                                >
                                  {normalCellValue}
                                </span>
                              </div>
                              <div
                                className={`cell-normal-plus-diagonal-bottom cell-split-remote-overtime${hasSingleValueInDiagonal ? " cell-split-remote-overtime--single" : ""}`}
                              >
                                {totalRemoteHoursForCell > 0 && (
                                  <div className="split-remote-half">
                                    {formatTime(totalRemoteHoursForCell)}
                                  </div>
                                )}
                                <div className="split-overtime-half">
                                  {formatTime(totalOvertimeHoursForCell)}
                                </div>
                              </div>
                            </div>
                          ) : hasThreeTypes ? (
                            <div className="cell-three-stripes-inner">
                              <div className="cell-stripe cell-stripe-normal">
                                <span
                                  className={
                                    hasNotSelfEntered ? "not-self-entered" : ""
                                  }
                                >
                                  {normalCellValue}
                                </span>
                              </div>
                              <div className="cell-stripe cell-stripe-remote">
                                {formatTime(remoteOnlyHours)}
                              </div>
                              <div className="cell-stripe cell-stripe-overtime">
                                {formatTime(overtimeOnlyHours + combinedHours)}
                              </div>
                            </div>
                          ) : hasRemoteAndOvertimeDiagonal ? (
                            <>
                              {totalRemoteHoursForCell > 0 && (
                                <div className="split-remote-half">
                                  {formatTime(totalRemoteHoursForCell)}
                                </div>
                              )}
                              <div className="split-overtime-half">
                                {formatTime(totalOvertimeHoursForCell)}
                              </div>
                            </>
                          ) : hasCombinedRemoteOvertime &&
                            !hasRemoteAndOvertimeDiagonal ? (
                            <div className="cell-combined-remote-overtime-value">
                              {formatTime(combinedHours)}
                            </div>
                          ) : hasDayOffAndRemote ? (
                            <>
                              <div className="split-dayoff-half">
                                {formatTime(dayOffHoursForCell)}
                              </div>
                              <div className="split-remote-half">
                                {formatTime(remoteOnlyHours)}
                              </div>
                            </>
                          ) : hasDayOffAndNormal ? (
                            <>
                              <div className="split-normal-half">
                                <span
                                  className={
                                    hasNotSelfEntered ? "not-self-entered" : ""
                                  }
                                >
                                  {normalCellValue}
                                </span>
                              </div>
                              <div className="split-dayoff-half">
                                {formatTime(dayOffHoursForCell)}
                              </div>
                            </>
                          ) : hasDayOffAndOvertime ? (
                            <>
                              <div className="split-dayoff-half">
                                {formatTime(dayOffHoursForCell)}
                              </div>
                              <div className="split-overtime-half">
                                {formatTime(overtimeHours)}
                              </div>
                            </>
                          ) : hasBothTypes ? (
                            <>
                              <div className="normal-hours">
                                <span
                                  className={
                                    hasNotSelfEntered ? "not-self-entered" : ""
                                  }
                                >
                                  {normalCellValue}
                                </span>
                              </div>
                              <div className="remote-hours">
                                {remoteCellValue}
                              </div>
                            </>
                          ) : overtimeHours > 0 ? (
                            <div className="cell-overtime-remote-stack">
                              {normalCellValue ? (
                                <div className="normal-hours">
                                  <span
                                    className={
                                      hasNotSelfEntered
                                        ? "not-self-entered"
                                        : ""
                                    }
                                  >
                                    {normalCellValue}
                                  </span>
                                </div>
                              ) : null}
                              {remoteReports.length > 0 ? (
                                <div className="remote-hours">
                                  {remoteCellValue}
                                </div>
                              ) : null}
                              <div className="overtime-hours">
                                {formatTime(overtimeHours)}
                              </div>
                            </div>
                          ) : (
                            <span
                              className={
                                hasNotSelfEntered ? "not-self-entered" : ""
                              }
                            >
                              {/* Если есть удаленные отчеты, показываем их часы; если только отгул — часы отгула */}
                              {remoteReports.length > 0
                                ? (() => {
                                    const remoteHours = remoteReports.reduce(
                                      (sum, report) => {
                                        if (
                                          !SPECIAL_JOB_TYPES[
                                            report.portal_job_type?.id
                                          ] &&
                                          !BUSINESS_TRIP_IDS.includes(
                                            report.portal_job_type?.id,
                                          )
                                        ) {
                                          return sum + report.time_spent / 3600;
                                        }
                                        return sum;
                                      },
                                      0,
                                    );
                                    return remoteHours > 0
                                      ? formatTime(remoteHours)
                                      : remoteCellValue;
                                  })()
                                : normalCellValue ||
                                  remoteCellValue ||
                                  (dayOffHoursForCell > 0
                                    ? formatTime(dayOffHoursForCell)
                                    : "")}
                            </span>
                          )}
                        </span>
                      </td>
                    );
                  })}
                  <td className={`total-hours`}>
                    {formatTime(
                      userReports.reduce((sum, report) => {
                        if (
                          !report.is_overtime &&
                          !SPECIAL_JOB_TYPES[report.portal_job_type?.id]
                        ) {
                          return sum + report.time_spent / 3600;
                        }
                        return sum;
                      }, 0),
                    )}
                  </td>
                  <td className={overtimeTotal > 0 ? "overtime-cell_true" : ""}>
                    {overtimeTotal > 0 ? formatTime(overtimeTotal) : ""}
                  </td>
                  <td className={dayOffTotal > 0 ? "dayoff-cell_true" : ""}>
                    {dayOffTotal > 0 ? formatTime(dayOffTotal) : ""}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {isLoadingTable && (
          <div className="table-loader-overlay">
            <div className="loader-content">
              <Preloader />
            </div>
          </div>
        )}
      </div>
    );
  };

  const isWeekend = (year, month, day) => {
    const date = new Date(Date.UTC(year, month, day, 12, 0, 0, 0));
    const dateString = date.toISOString().split("T")[0];
    const dayData = calendarData[dateString];
    console.log(`Checking weekend for ${dateString}:`, {
      dayData,
      isWeekend: !dayData?.workday_flag,
    });
    return !dayData?.workday_flag;
  };

  const customStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: "10px",
      border: "1px solid #E0E0E0",
      padding: "5px",
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      lineHeight: "19.12px",
      width: "392px",
      opacity: state.isLoading || state.isDisabled ? 0.7 : 1,
      cursor: state.isLoading || state.isDisabled ? "not-allowed" : "pointer",
      "&:hover": {
        borderColor: state.isLoading ? "#E0E0E0" : "#999",
      },
    }),
    menu: (provided) => ({
      ...provided,
      width: "392px",
      borderRadius: "10px",
      marginTop: "5px",
    }),
    option: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
    }),
    container: (provided) => ({
      ...provided,
      width: "392px",
    }),
    loadingMessage: (provided) => ({
      ...provided,
      fontFamily: "Manrope",
      fontSize: "14px",
      fontWeight: 300,
      color: "#666",
    }),
    loadingIndicator: (provided) => ({
      ...provided,
      color: "#999",
    }),
  };

  // Функция для проверки статуса согласования для текущего отдела и месяца
  const isCurrentTimesheetOnApproval = () => {
    const hasStatus = currentTimesheetStatus !== null;
    console.log("🔍 isCurrentTimesheetOnApproval:", {
      hasStatus,
      currentTimesheetStatus,
    });
    return hasStatus;
  };

  // Функция для проверки доступа к согласованию табелей
  const canApproveTimesheet = () => {
    if (!userData) return false;

    return userData.access_levels.some(
      (level) => level.name === "АДМС" || level.name === "ТАБ",
    );
  };

  // Функция для проверки доступа к выгрузке в Excel
  const canExportToExcel = () => {
    if (!userData) return false;

    return userData.access_levels.some((level) => level.name === "АДМС");
  };

  // Функция для загрузки пользователей для согласования
  const loadApprovalUsers = async () => {
    try {
      const userIds = [62, 131]; // ID пользователей для согласования
      const usersData = await Promise.all(
        userIds.map(async (id) => {
          const response = await getUsersSummary({
            userId: id,
            pageSize: 1,
          });
          const user = response.users[0];
          if (user) {
            // Добавляем информацию о роли и обязательности
            if (id === 62) {
              user.role = "Финансовый директор";
              user.isRequired = true;
            } else if (id === 131) {
              user.role = "HR менеджер";
              user.isRequired = true;
            }
          }
          return user;
        }),
      );
      const filteredUsers = usersData.filter((user) => user); // Убираем undefined
      setApprovalUsers(filteredUsers);

      // Автоматически выбираем всех обязательных пользователей
      // (в данном случае все пользователи обязательные, но это можно расширить)
      console.log("Загружены пользователи для согласования:", filteredUsers);
    } catch (error) {
      console.error(
        "Ошибка при загрузке пользователей для согласования:",
        error,
      );
    }
  };

  // Функция для отправки табеля на согласование
  const handleSendForApproval = async () => {
    // Дополнительная проверка перед открытием модального окна
    if (isCurrentTimesheetOnApproval()) {
      alert("Табель для этого отдела и месяца уже отправлен на согласование!");
      return;
    }

    // Проверяем еще раз на сервере для надежности
    try {
      const existingTimesheets = await getTimesheetApprovals({
        group_id: selectedDepartment.id,
        year: selectedMonth.getFullYear(),
        month: selectedMonth.getMonth() + 1,
      });

      if (hasExistingTimesheet(existingTimesheets)) {
        // Обновляем локальный статус
        await loadTimesheetStatus();

        const existingStatus = existingTimesheets[0].status;
        alert(
          `Табель для этого отдела и месяца уже существует!\nТекущий статус: ${existingStatus}\n\nНевозможно создать новое согласование.`,
        );
        return;
      }
    } catch (error) {
      console.error("Ошибка при проверке статуса табеля:", error);
      alert("Ошибка при проверке статуса табеля. Попробуйте еще раз.");
      return;
    }

    // Проверяем наличие проблемных ячеек
    const hasProblems = hasProblematicCells();

    if (hasProblems && !showConfirmationTooltip) {
      // Есть проблемы и еще не показывали предупреждение - показываем тултип
      console.log("⚠️ Обнаружены проблемные ячейки, показываем предупреждение");
      setShowConfirmationTooltip(true);
      setHighlightProblems(true);
    } else {
      // Нет проблем или пользователь уже подтвердил - открываем модальное окно
      console.log(
        hasProblems
          ? "✅ Пользователь подтвердил отправку несмотря на проблемы"
          : "✅ Проблемных ячеек не обнаружено, открываем модальное окно",
      );
      setShowConfirmationTooltip(false);
      setHighlightProblems(false);
      setShowApprovalModal(true);
      loadApprovalUsers();
    }
  };

  // Функция для отмены отправки
  const handleCancelApproval = () => {
    setShowConfirmationTooltip(false);
    setHighlightProblems(false);
  };

  // Функция для закрытия модального окна согласования
  const handleCloseApprovalModal = () => {
    setShowApprovalModal(false);
    setApprovalUsers([]);
  };

  // Функция для отправки на согласование
  const handleSubmitApproval = async () => {
    try {
      console.log("Отправка табеля на согласование:", {
        department: selectedDepartment?.name,
        month: selectedMonth,
        users: approvalUsers,
      });

      if (!selectedDepartment || approvalUsers.length === 0) {
        alert("Ошибка: не выбран отдел или согласующие");
        return;
      }

      // Сначала проверяем, нет ли уже активного согласования
      const existingTimesheets = await getTimesheetApprovals({
        group_id: selectedDepartment.id,
        year: selectedMonth.getFullYear(),
        month: selectedMonth.getMonth() + 1,
      });

      if (hasExistingTimesheet(existingTimesheets)) {
        const existingStatus = existingTimesheets[0].status;
        alert(
          `Табель для этого отдела и месяца уже существует!\nТекущий статус: ${existingStatus}\n\nНевозможно создать новое согласование.`,
        );
        handleCloseApprovalModal();
        return;
      }

      // Создаем запрос на согласование через API
      const timesheetData = {
        year: selectedMonth.getFullYear(),
        month: selectedMonth.getMonth() + 1, // JavaScript месяцы начинаются с 0
        group_id: selectedDepartment.id,
        approver_ids: approvalUsers.map((user) => user.id),
      };

      console.log(
        "Данные для создания запроса на согласование:",
        timesheetData,
      );

      const result = await createTimesheetApproval(timesheetData);

      console.log("Запрос на согласование создан:", result);

      // Обновляем статус табеля с сервера
      await loadTimesheetStatus();

      // Показываем уведомление об успехе
      setShowSuccessNotification(true);

      // Автоматически скрываем уведомление через 5 секунд
      setTimeout(() => {
        setShowSuccessNotification(false);
      }, 5000);

      handleCloseApprovalModal();
    } catch (error) {
      console.error("Ошибка при отправке на согласование:", error);

      // Проверяем, не вернул ли сервер информацию о том, что согласование уже существует
      if (error.message && error.message.includes("400")) {
        await loadTimesheetStatus(); // Перезагружаем статус
        alert(
          "Табель для этого отдела и месяца уже существует!\n\nОбновите страницу для актуализации данных.",
        );
      } else {
        alert(
          `Ошибка при отправке на согласование: ${
            error.message || "Неизвестная ошибка"
          }`,
        );
      }
    }
  };

  // Функция для проверки проблемных ячеек
  const isProblematicCell = (dayReports, dayData) => {
    if (!highlightProblems) return false;

    // Проверяем пустые ячейки (нет отчетов и не выходной)
    if (!dayReports || dayReports.length === 0) {
      return !dayData.isWeekend;
    }

    // Проверяем ячейки с более чем 12 часами
    const totalHours = dayReports.reduce((sum, report) => {
      if (
        !SPECIAL_JOB_TYPES[report.portal_job_type?.id] &&
        !report.is_overtime
      ) {
        return sum + report.time_spent / 3600;
      }
      return sum;
    }, 0);

    return totalHours > 12;
  };

  // Функция для проверки наличия проблемных ячеек во всем табеле
  const hasProblematicCells = () => {
    const daysInMonth = generateTableHeaders();

    // Фильтруем пользователей: проверяем только тех, у кого есть отчеты за выбранный месяц
    const usersWithReportsForCheck = departmentUsers.filter((user) => {
      const userReports = reports.filter((report) => {
        const reportDate = new Date(report.fact_date_start);
        return (
          report.user.id === user.id &&
          reportDate.getMonth() === selectedMonth.getMonth() &&
          reportDate.getFullYear() === selectedMonth.getFullYear()
        );
      });
      return userReports.length > 0;
    });

    for (const user of usersWithReportsForCheck) {
      for (const dayData of daysInMonth) {
        const dayReports = reports.filter((report) => {
          const reportDate = new Date(report.fact_date_start);
          return (
            report.user.id === user.id &&
            reportDate.getMonth() === selectedMonth.getMonth() &&
            reportDate.getFullYear() === selectedMonth.getFullYear() &&
            reportDate.getDate() === dayData.day
          );
        });

        // Проверяем пустые ячейки (нет отчетов и не выходной)
        if ((!dayReports || dayReports.length === 0) && !dayData.isWeekend) {
          return true;
        }

        // Проверяем ячейки с более чем 12 часами
        const totalHours = dayReports.reduce((sum, report) => {
          if (
            !SPECIAL_JOB_TYPES[report.portal_job_type?.id] &&
            !report.is_overtime
          ) {
            return sum + report.time_spent / 3600;
          }
          return sum;
        }, 0);

        if (totalHours > 12) {
          return true;
        }
      }
    }

    return false;
  };

  const exportToExcel = async () => {
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Табель");

    // Формируем заголовки
    const daysInMonth = generateTableHeaders();
    const headerRow = [
      "ФИО",
      ...daysInMonth.map((dayData) => {
        const date = new Date(
          selectedMonth.getFullYear(),
          selectedMonth.getMonth(),
          dayData.day,
        );
        return date.toLocaleDateString("ru", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
        });
      }),
      "Итого",
      "Переработки",
      "Отгулы",
    ];
    worksheet.addRow(headerRow);

    // Фильтруем пользователей: оставляем только тех, у кого есть отчеты за выбранный месяц
    const usersWithReportsForExport = departmentUsers.filter((user) => {
      const userReports = reports.filter((report) => {
        const reportDate = new Date(report.fact_date_start);
        return (
          report.user.id === user.id &&
          reportDate.getMonth() === selectedMonth.getMonth() &&
          reportDate.getFullYear() === selectedMonth.getFullYear()
        );
      });
      return userReports.length > 0;
    });

    // Добавляем данные по каждому сотруднику
    usersWithReportsForExport.forEach((user) => {
      const userRow = [`${user.surname} ${user.name}`];
      let userTotal = 0;
      let userOvertimeTotal = 0;
      let userDayOffTotal = 0;

      // Получаем отчеты пользователя
      const userReports = reports.filter((report) => {
        const reportDate = new Date(report.fact_date_start);
        return (
          report.user.id === user.id &&
          reportDate.getMonth() === selectedMonth.getMonth() &&
          reportDate.getFullYear() === selectedMonth.getFullYear()
        );
      });

      daysInMonth.forEach((dayData) => {
        const dayReports = userReports.filter((report) => {
          const reportDate = new Date(report.fact_date_start);
          return reportDate.getDate() === dayData.day;
        });

        // Обычные отчеты (не переработки, не отгулы)
        const normalReports = dayReports.filter(
          (report) => !report.is_overtime && !report.is_day_off,
        );
        // Переработки
        const overtimeReports = dayReports.filter(
          (report) => report.is_overtime && isOvertimeApproved(report),
        );
        // Отгулы
        const dayOffReports = dayReports.filter((report) => report.is_day_off);
        // Спец. типы работ
        const specialReport = dayReports.find(
          (report) => SPECIAL_JOB_TYPES[report.portal_job_type?.id],
        );

        // Часы
        const normalHours = normalReports.reduce(
          (sum, report) => sum + (report.time_spent || 0) / 3600,
          0,
        );
        const overtimeHours = overtimeReports.reduce(
          (sum, report) => sum + (report.time_spent || 0) / 3600,
          0,
        );
        const dayOffHours = dayOffReports.reduce(
          (sum, report) => sum + (report.time_spent || 0) / 3600,
          0,
        );

        // Если есть спец. тип работы — только код, часы не идут в итог
        if (specialReport) {
          userRow.push(
            SPECIAL_JOB_TYPES[specialReport.portal_job_type.id].code,
          );
        } else {
          // Итоговое значение в ячейке (без переработок)
          const cellHours = normalHours + dayOffHours;
          userRow.push(cellHours > 0 ? formatTime(cellHours) : "");
          userTotal += cellHours;
        }
        userOvertimeTotal += overtimeHours;
        userDayOffTotal += dayOffHours;
      });

      userRow.push(formatTime(userTotal));
      userRow.push(userOvertimeTotal > 0 ? formatTime(userOvertimeTotal) : "");
      userRow.push(userDayOffTotal > 0 ? formatTime(userDayOffTotal) : "");
      worksheet.addRow(userRow);
    });

    // Применяем стили и форматирование
    worksheet.eachRow((row, rowNumber) => {
      row.eachCell((cell, colNumber) => {
        // Базовые стили для всех ячеек
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };

        // Для первой строки (заголовков)
        if (rowNumber === 1) {
          if (colNumber === 1) {
            cell.alignment = { vertical: "middle", horizontal: "center" };
          } else {
            cell.alignment = {
              textRotation: 90,
              vertical: "bottom",
              horizontal: "center",
            };
          }
          cell.font = { bold: true };
        } else {
          cell.alignment = { vertical: "middle", horizontal: "center" };

          // Для итоговых столбцов
          if (colNumber === worksheet.columnCount - 2) {
            // Для столбца "Итого" - светло-зеленый
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFDAF5E9" },
            };
          } else if (colNumber === worksheet.columnCount - 1) {
            // Для столбца "Переработки" - оранжевый
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFFDE9D9" },
            };
          } else if (colNumber === worksheet.columnCount) {
            // Для столбца "Отгулы" - более яркий синий
            cell.fill = {
              type: "pattern",
              pattern: "solid",
              fgColor: { argb: "FFB3E5FC" }, // Более яркий синий цвет
            };
          }
          // Для остальных ячеек с данными (не ФИО и не итоги)
          else if (colNumber > 1 && colNumber < worksheet.columnCount - 2) {
            const day = colNumber - 1;
            const date = new Date(
              selectedMonth.getFullYear(),
              selectedMonth.getMonth(),
              day,
            );
            const dayReports = reports.filter((report) => {
              const reportDate = new Date(report.fact_date_start);
              return (
                reportDate.getDate() === day &&
                reportDate.getMonth() === selectedMonth.getMonth() &&
                reportDate.getFullYear() === selectedMonth.getFullYear() &&
                report.user?.id === departmentUsers[rowNumber - 2].id
              );
            });
            const normalReports = dayReports.filter(
              (report) => !report.is_overtime && !report.is_day_off,
            );
            const overtimeReports = dayReports.filter(
              (report) => report.is_overtime && isOvertimeApproved(report),
            );
            const dayOffReports = dayReports.filter(
              (report) => report.is_day_off,
            );
            const specialReport = dayReports.find(
              (report) => SPECIAL_JOB_TYPES[report.portal_job_type?.id],
            );
            const normalHours = normalReports.reduce(
              (sum, report) => sum + (report.time_spent || 0) / 3600,
              0,
            );
            const overtimeHours = overtimeReports.reduce(
              (sum, report) => sum + (report.time_spent || 0) / 3600,
              0,
            );
            const dayOffHours = dayOffReports.reduce(
              (sum, report) => sum + (report.time_spent || 0) / 3600,
              0,
            );
            // Если спец. тип — цвет и стиль для него
            if (specialReport) {
              const code =
                SPECIAL_JOB_TYPES[specialReport.portal_job_type.id].code;
              if (code === "БЛ") {
                cell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFFFEBEE" },
                };
                cell.font = { color: { argb: "FFB71C1C" }, bold: true };
              } else if (code === "ОТ") {
                cell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFE3F2FD" },
                };
                cell.font = { color: { argb: "FF0D47A1" }, bold: true };
              } else if (code === "БС") {
                cell.fill = {
                  type: "pattern",
                  pattern: "solid",
                  fgColor: { argb: "FFFFF3E0" },
                };
                cell.font = { color: { argb: "FFE65100" }, bold: true };
              }
            } else if (dayOffHours > 0 && overtimeHours > 0) {
              cell.fill = {
                type: "pattern",
                pattern: "darkTrellis",
                fgColor: { argb: "FFB3E5FC" }, // отгул
                bgColor: { argb: "FFFDE9D9" }, // переработка
              };
            } else if (dayOffHours > 0) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFB3E5FC" },
              };
            } else if (overtimeHours > 0) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFDE9D9" },
              };
            } else if (
              isWeekend(date.getFullYear(), date.getMonth(), date.getDate())
            ) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFD3ADE7" },
              };
            } else if (normalHours > 0) {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFDAF5E9" },
              };
            } else {
              cell.fill = {
                type: "pattern",
                pattern: "solid",
                fgColor: { argb: "FFFCD5FD" },
              };
            }
          }
        }
      });
    });

    // Устанавливаем ширину столбцов
    worksheet.getColumn(1).width = 30; // ФИО
    for (let i = 2; i <= worksheet.columnCount; i++) {
      worksheet.getColumn(i).width = 6;
    }

    // Устанавливаем высоту первой строки для вертикального текста
    worksheet.getRow(1).height = 60;

    // Сохраняем файл
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Табель_${selectedMonth.toLocaleDateString("ru", {
      month: "long",
      year: "numeric",
    })}.xlsx`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="department-timesheet">
      <h2 className="department-timesheet__title">Табель отдела:</h2>
      {isLoadingDepartments || isLoading ? (
        <div className="department-timesheet__loader">
          <Preloader />
        </div>
      ) : (
        <>
          <div className="department-timesheet__top-section">
            <div className="department-timesheet__left-block">
              <div className="department-timesheet__controls-row">
                <div className="department-timesheet__select-wrapper">
                  <Select
                    options={availableDepartments.map((dept) => ({
                      value: dept.id,
                      label: dept.name,
                    }))}
                    value={
                      selectedDepartment
                        ? {
                            value: selectedDepartment.id,
                            label: selectedDepartment.name,
                          }
                        : null
                    }
                    onChange={(option) =>
                      setSelectedDepartment(
                        availableDepartments.find((d) => d.id === option.value),
                      )
                    }
                    isDisabled={!canSelectDepartment}
                    placeholder={
                      canSelectDepartment ? "Выберите отдел" : "Отдел"
                    }
                    styles={customStyles}
                    className="department-timesheet__select"
                  />
                </div>
                <div className="department-timesheet__datepicker-wrapper">
                  <DatePicker
                    selected={selectedMonth}
                    onChange={setSelectedMonth}
                    dateFormat="MM/yyyy"
                    showMonthYearPicker
                    locale="ru"
                    className="department-timesheet__datepicker"
                    calendarClassName="department-timesheet__calendar"
                  />
                </div>
              </div>
              <div className="department-timesheet__buttons-row">
                {/* Кнопка выгрузки в Excel - доступна для пользователей с доступом АДМС */}
                {canExportToExcel() && (
                  <button
                    className="department-timesheet__export-button"
                    onClick={exportToExcel}
                    disabled={!selectedDepartment}
                  >
                    Выгрузить в Excel
                  </button>
                )}

                {/* Кнопки для работы с согласованием табеля */}
                {canApproveTimesheet() && (
                  <div
                    className="department-timesheet__approval-buttons"
                    style={{ position: "relative" }}
                  >
                    <button
                      className="department-timesheet__export-button"
                      onClick={handleSendForApproval}
                      disabled={
                        !selectedDepartment || isCurrentTimesheetOnApproval()
                      }
                    >
                      {isCurrentTimesheetOnApproval()
                        ? "Табель уже существует"
                        : showConfirmationTooltip
                          ? "Подтвердить отправку"
                          : "Отправить табель на согласование"}
                    </button>

                    {/* Тултип с подтверждением */}
                    {showConfirmationTooltip && (
                      <div
                        style={{
                          position: "absolute",
                          top: "-60px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          backgroundColor: "#fff",
                          border: "2px solid #ff6b6b",
                          borderRadius: "8px",
                          padding: "12px 16px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                          zIndex: 1000,
                          minWidth: "280px",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            marginBottom: "12px",
                            fontWeight: "bold",
                            color: "#333",
                          }}
                        >
                          Вы уверены, что хотите отправить табель на
                          согласование?
                        </div>
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#666",
                            marginBottom: "8px",
                          }}
                        >
                          Проблемные ячейки подсвечены красным
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: "8px",
                            justifyContent: "center",
                          }}
                        >
                          <button
                            onClick={handleSendForApproval}
                            style={{
                              backgroundColor: "#28a745",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              padding: "6px 12px",
                              cursor: "pointer",
                              fontSize: "12px",
                            }}
                          >
                            Да, отправить
                          </button>
                          <button
                            onClick={handleCancelApproval}
                            style={{
                              backgroundColor: "#dc3545",
                              color: "white",
                              border: "none",
                              borderRadius: "4px",
                              padding: "6px 12px",
                              cursor: "pointer",
                              fontSize: "12px",
                            }}
                          >
                            Отмена
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="department-timesheet__legend-block">
              <div className="report-status-legend">
                <div className="legend-title">Статус отчета:</div>
                <div className="legend-items">
                  <div className="legend-item">
                    <div className="legend-color regular-report"></div>
                    <span>Обычный отчет</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color overtime-cell_true"></div>
                    <span>Переработка</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color dayoff-cell_true"></div>
                    <span>Отгул</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color bl"></div>
                    <span>БЛ - Больничный</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color ot"></div>
                    <span>ОТ - Отпуск</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color bs"></div>
                    <span>БС - Отпуск без содержания</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color business-trip"></div>
                    <span>Командировка</span>
                  </div>
                  <div className="legend-item">
                    <div className="legend-color remote-cell_true"></div>
                    <span>Удаленно</span>
                  </div>
                  <div className="legend-item">
                    <span
                      className="not-self-entered"
                      style={{ fontWeight: "bold", color: "#d32f2f" }}
                    >
                      часы
                    </span>
                    <span>— отчет создан администратором</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {selectedDepartment && (
            <>
              {/* Баннер статуса согласования */}
              {isCurrentTimesheetOnApproval() && currentTimesheetStatus && (
                <div className="timesheet-approval-status-banner">
                  <div className="timesheet-approval-status-banner__content">
                    <div className="timesheet-approval-status-banner__icon">
                      📋
                    </div>
                    <div className="timesheet-approval-status-banner__text">
                      <div className="timesheet-approval-status-banner__title">
                        Табель для этого периода уже существует (Статус:{" "}
                        {currentTimesheetStatus.status})
                      </div>
                      <div className="timesheet-approval-status-banner__subtitle">
                        {currentTimesheetStatus.initiator && (
                          <>
                            Инициатор: {currentTimesheetStatus.initiator.name}{" "}
                            {currentTimesheetStatus.initiator.surname}
                            {currentTimesheetStatus.created_at && (
                              <>
                                {" "}
                                •{" "}
                                {new Date(
                                  currentTimesheetStatus.created_at,
                                ).toLocaleString("ru", {
                                  day: "2-digit",
                                  month: "2-digit",
                                  year: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })}
                              </>
                            )}
                            <br />
                          </>
                        )}
                        {currentTimesheetStatus.steps &&
                          currentTimesheetStatus.steps.length > 0 && (
                            <>
                              Согласующие:{" "}
                              {currentTimesheetStatus.steps
                                .map(
                                  (step) =>
                                    `${step.approver.name} ${step.approver.surname}`,
                                )
                                .join(", ")}
                            </>
                          )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {renderTimesheet()}
            </>
          )}
        </>
      )}

      {/* Модальное окно согласования табеля */}
      {showApprovalModal && (
        <div
          className="timesheet-approval-modal-overlay"
          onClick={handleCloseApprovalModal}
        >
          <div
            className="timesheet-approval-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="timesheet-approval-modal__header">
              <h3 className="timesheet-approval-modal__title">
                Согласование табеля
              </h3>
            </div>

            <div className="timesheet-approval-modal__content">
              <div className="timesheet-approval-modal__info">
                <div className="timesheet-approval-modal__department">
                  {selectedDepartment?.name}
                </div>
                <div className="timesheet-approval-modal__period">
                  {selectedMonth.toLocaleDateString("ru", {
                    month: "long",
                    year: "numeric",
                  })}
                </div>
              </div>

              <div className="timesheet-approval-modal__warning">
                <span className="timesheet-approval-modal__warning-icon">
                  ⚠️
                </span>
                <span className="timesheet-approval-modal__warning-text">
                  Вы отправляете на согласование табель за выбранный период для
                  всего отдела.
                </span>
              </div>

              <div className="timesheet-approval-modal__recipients">
                <div className="timesheet-approval-modal__recipients-title">
                  Выберите кому отправить на согласование:
                </div>

                <div className="timesheet-approval-modal__recipients-list">
                  {approvalUsers.map((user, index) => (
                    <div
                      key={user.id}
                      className={`timesheet-approval-modal__recipient ${
                        user.isRequired
                          ? "timesheet-approval-modal__recipient--required"
                          : ""
                      }`}
                    >
                      <div className="timesheet-approval-modal__checkbox">
                        <input
                          type="checkbox"
                          id={`timesheet-recipient-${user.id}`}
                          defaultChecked
                          disabled={user.isRequired}
                          readOnly
                        />
                        <label
                          htmlFor={`timesheet-recipient-${user.id}`}
                          className={`timesheet-approval-modal__checkbox-label ${
                            user.isRequired
                              ? "timesheet-approval-modal__checkbox-label--disabled"
                              : ""
                          }`}
                        >
                          <span className="timesheet-approval-modal__recipient-role">
                            {user.role} {user.isRequired ? "(обязательно)" : ""}
                            {user.isRequired && (
                              <span
                                className="timesheet-approval-modal__lock-icon"
                                title="Заблокировано"
                              >
                                🔒
                              </span>
                            )}
                          </span>
                          <span className="timesheet-approval-modal__recipient-name">
                            {user.surname} {user.name.charAt(0)}.
                          </span>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <div className="timesheet-approval-modal__actions">
              <button
                className="timesheet-approval-modal__cancel-btn"
                onClick={handleCloseApprovalModal}
              >
                Отмена
              </button>
              <button
                className="timesheet-approval-modal__submit-btn"
                onClick={handleSubmitApproval}
              >
                Отправить
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Уведомление об успешной отправке */}
      {showSuccessNotification && (
        <div className="timesheet-success-notification">
          <div className="timesheet-success-notification__content">
            <div className="timesheet-success-notification__icon">✅</div>
            <div className="timesheet-success-notification__text">
              <div className="timesheet-success-notification__title">
                Табель успешно отправлен на согласование!
              </div>
              <div className="timesheet-success-notification__subtitle">
                Редактирование и создание отчетов для пользователей закрыты
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentReports;
